<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useTrack } from 'dashboard/composables';
import { useAlert } from 'dashboard/composables';
import LinearAPI from 'dashboard/api/integrations/linear';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { parseLinearAPIErrorResponse } from 'dashboard/store/utils/api';
import { LINEAR_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  title: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const issues = ref([]);
const selectedIssueId = ref('');
const selectedIssueLabel = ref('');
const isFetching = ref(false);
const isLinking = ref(false);

const issueOptions = computed(() =>
  issues.value.map(issue => ({ value: issue.id, label: issue.name }))
);

const isSubmitDisabled = computed(() => {
  return !selectedIssueId.value || isLinking.value;
});

const onSelectIssue = value => {
  selectedIssueId.value = value;
  const selected = issueOptions.value.find(option => option.value === value);
  selectedIssueLabel.value = selected ? selected.label : '';
};

const onClose = () => {
  emit('close');
};

const searchIssues = async value => {
  try {
    isFetching.value = true;
    const response = await LinearAPI.searchIssues(value);
    issues.value = response.data.map(issue => ({
      id: issue.identifier,
      name: `${issue.identifier} ${issue.title}`,
    }));
  } catch (error) {
    const errorMessage = parseLinearAPIErrorResponse(
      error,
      t('INTEGRATION_SETTINGS.LINEAR.LINK.ERROR')
    );
    useAlert(errorMessage);
  } finally {
    isFetching.value = false;
  }
};

const onSearch = debounce(value => {
  issues.value = [];
  if (!value) return;
  searchIssues(value);
}, 300);

const linkIssue = async () => {
  const issueId = selectedIssueId.value;
  try {
    isLinking.value = true;
    await LinearAPI.link_issue(props.conversationId, issueId, props.title);
    useAlert(t('INTEGRATION_SETTINGS.LINEAR.LINK.LINK_SUCCESS'));
    issues.value = [];
    onClose();
    useTrack(LINEAR_EVENTS.LINK_ISSUE);
  } catch (error) {
    const errorMessage = parseLinearAPIErrorResponse(
      error,
      t('INTEGRATION_SETTINGS.LINEAR.LINK.LINK_ERROR')
    );
    useAlert(errorMessage);
  } finally {
    isLinking.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col justify-between gap-2">
    <ComboBox
      :model-value="selectedIssueId"
      :options="issueOptions"
      :display-label="selectedIssueLabel"
      :placeholder="$t('INTEGRATION_SETTINGS.LINEAR.LINK.SELECT')"
      :search-placeholder="$t('INTEGRATION_SETTINGS.LINEAR.LINK.SEARCH')"
      :empty-state="
        isFetching ? $t('INTEGRATION_SETTINGS.LINEAR.LINK.LOADING') : ''
      "
      use-api-results
      @update:model-value="onSelectIssue"
      @search="onSearch"
    />
    <div class="flex items-center justify-end w-full gap-2 mt-2">
      <Button
        faded
        slate
        type="reset"
        :label="$t('INTEGRATION_SETTINGS.LINEAR.ADD_OR_LINK.CANCEL')"
        @click.prevent="onClose"
      />
      <Button
        type="submit"
        :label="$t('INTEGRATION_SETTINGS.LINEAR.LINK.TITLE')"
        :disabled="isSubmitDisabled"
        :is-loading="isLinking"
        @click.prevent="linkIssue"
      />
    </div>
  </div>
</template>
